import React, { useCallback, useEffect, useState } from 'react';
import { Waves, X, Images, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { setSeoMetadata } from '../../lib/seo';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import { fetchJsonCached } from '../../lib/settingsCache';
import { useSiteFeatureFlags } from '../../hooks/useSiteFeatureFlags';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Two-step gallery:
 *  1. the "See Gallery" button opens this pop-up showing the beach's thumbnails
 *  2. clicking a thumbnail opens that photo full size, with arrow navigation
 */
const GalleryModal = ({ beach, onClose }) => {
  const [lightbox, setLightbox] = useState(-1);
  const photos = beach?.gallery || [];

  const step = useCallback((delta) => {
    setLightbox((current) => {
      if (current < 0) return current;
      const next = current + delta;
      if (next < 0) return photos.length - 1;
      if (next >= photos.length) return 0;
      return next;
    });
  }, [photos.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (lightbox >= 0) setLightbox(-1);
        else onClose();
      }
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, lightbox, step]);

  if (!beach) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true"
      aria-label={`${beach.name} gallery`} data-testid="beach-gallery-modal">
      <div className="absolute inset-0 bg-black/75" onClick={onClose} data-testid="beach-gallery-overlay" />

      <div className="relative z-10 w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 truncate flex items-center gap-2">
            <Images className="w-5 h-5 text-blue-600" />
            {beach.name}
            <span className="text-sm font-normal text-slate-500">({photos.length} photos)</span>
          </h2>
          <button onClick={onClose} className="p-2 rounded text-slate-600 hover:text-slate-900 hover:bg-slate-200"
            aria-label="Close gallery" data-testid="beach-gallery-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {photos.map((url, index) => (
              <button key={`${url}-${index}`} onClick={() => setLightbox(index)}
                className="group rounded-lg overflow-hidden border border-slate-200 bg-slate-100"
                aria-label={`Open photo ${index + 1}`} data-testid={`beach-gallery-thumb-${index + 1}`}>
                <img src={resolveMediaUrl(url)} alt={`${beach.name} ${index + 1}`} loading="lazy"
                  className="w-full h-28 object-cover group-hover:scale-105 transition-transform duration-300" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* full-size lightbox */}
      {lightbox >= 0 && photos[lightbox] && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/90 p-4"
          data-testid="beach-lightbox">
          <button onClick={() => setLightbox(-1)}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white"
            aria-label="Close photo" data-testid="beach-lightbox-close">
            <X className="w-7 h-7" />
          </button>
          {photos.length > 1 && (
            <>
              <button onClick={() => step(-1)} className="absolute left-3 p-2 text-white/80 hover:text-white"
                aria-label="Previous photo" data-testid="beach-lightbox-prev">
                <ChevronLeft className="w-9 h-9" />
              </button>
              <button onClick={() => step(1)} className="absolute right-3 p-2 text-white/80 hover:text-white"
                aria-label="Next photo" data-testid="beach-lightbox-next">
                <ChevronRight className="w-9 h-9" />
              </button>
            </>
          )}
          <figure className="max-w-5xl w-full text-center">
            <img src={resolveMediaUrl(photos[lightbox])} alt={`${beach.name} ${lightbox + 1}`}
              className="max-h-[78vh] w-auto mx-auto object-contain rounded-lg shadow-2xl"
              data-testid="beach-lightbox-image" />
            <figcaption className="mt-3 text-white/80 text-sm">
              {beach.name} &mdash; {lightbox + 1} / {photos.length}
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
};

const BeachInfoPage = () => {
  const { beaches_enabled, _loaded } = useSiteFeatureFlags();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [galleryBeach, setGalleryBeach] = useState(null);

  useEffect(() => {
    setSeoMetadata({
      title: 'St John Beach Info | Beaches & Snorkeling Guide | St John USVI',
      description:
        'Guide to the beaches of St John, USVI. North shore and south shore beaches with snorkeling notes, reef conditions, access tips and photo galleries.',
      keywords:
        'st john beaches, trunk bay, maho bay, cinnamon bay, salt pond bay, st john snorkeling, beach info st john usvi',
      canonicalPath: '/island-info/beach-info-st-john',
      ogType: 'website',
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetchJsonCached(`${API_URL}/api/public/beaches`);
        if (!cancelled) setData(res);
      } catch (error) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const page = data?.page || {};
  const sections = data?.sections || [];
  let rowIndex = 0;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-28 lg:pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4">
          <header className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold italic uppercase text-slate-900 tracking-tight">
              Island Beach Info
            </h1>
            <p className="font-script text-3xl text-cyan-500 mt-3">St John, Virgin Islands</p>
          </header>

          {loading || !_loaded ? (
            <div className="flex justify-center py-16" data-testid="beach-loading">
              <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !beaches_enabled ? (
            <p className="text-center text-slate-500 py-16" data-testid="beach-disabled">
              Our beach guide is being updated. Please check back soon.
            </p>
          ) : (
            <>
              {(page.intro_title || page.intro_body) && (
                <section className="max-w-4xl mx-auto text-center mb-14" data-testid="beach-intro">
                  {page.intro_title && (
                    <h2 className="text-3xl font-extrabold italic uppercase text-slate-900 tracking-tight">
                      {page.intro_title}
                    </h2>
                  )}
                  {page.intro_body && (
                    <p className="mt-4 text-slate-700 leading-relaxed">{page.intro_body}</p>
                  )}
                </section>
              )}

              {sections.length === 0 ? (
                <p className="text-center text-slate-500 py-16" data-testid="beach-empty">
                  Beaches are being added. Check back soon!
                </p>
              ) : (
                sections.map((section) => (
                  <section key={section.key} className="mb-16" data-testid={`beach-section-${section.key}`}>
                    {section.title && (
                      <h2 className="font-script text-4xl sm:text-5xl text-red-600 text-center mb-10">
                        {section.title}
                      </h2>
                    )}

                    <div className="space-y-12 lg:space-y-16">
                      {section.beaches.map((beach) => {
                        /* same alternating panel treatment as the hiking page */
                        const panel = rowIndex++ % 2 === 0 ? 'bg-[#3f3d9e]' : 'bg-[#2e9ab5]';
                        return (
                          <article key={beach.id}
                            className="grid lg:grid-cols-[minmax(0,1fr)_240px] gap-8 lg:gap-10 items-start"
                            data-testid={`beach-row-${beach.slug}`}>
                            <div className="relative flex flex-col sm:flex-row items-stretch">
                              <div className="relative z-10 sm:w-[46%] shrink-0 shadow-xl">
                                {beach.image_url ? (
                                  <img src={resolveMediaUrl(beach.image_url)} alt={beach.name} loading="lazy"
                                    className="w-full h-56 sm:h-full min-h-[240px] object-cover" />
                                ) : (
                                  <div className="w-full h-56 sm:h-full min-h-[240px] bg-slate-200 flex items-center justify-center text-slate-400">
                                    <Waves className="w-10 h-10" />
                                  </div>
                                )}
                              </div>

                              <div className={`${panel} flex-1 shadow-2xl px-6 py-8 sm:-ml-10 sm:pl-16 sm:pr-8 flex flex-col justify-center`}>
                                <h3 className="text-2xl lg:text-3xl font-bold text-white">{beach.name}</h3>
                                {beach.description && (
                                  <div className="mt-4 space-y-3 text-white/90 leading-relaxed text-sm lg:text-base">
                                    {beach.description.split('\n\n').map((para, i) => (
                                      <p key={i}>{para}</p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* See Gallery + optional map */}
                            <div className="flex flex-col items-center lg:items-start gap-4">
                              {beach.gallery_count > 0 && (
                                <button onClick={() => setGalleryBeach(beach)}
                                  className="group inline-flex flex-col items-center"
                                  aria-label={`${beach.name} gallery`}
                                  data-testid={`beach-gallery-btn-${beach.slug}`}>
                                  <span className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-700 shadow-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <Images className="w-8 h-8 text-white" />
                                  </span>
                                  <span className="mt-1.5 text-sm font-bold text-slate-900 group-hover:text-blue-700">
                                    See Gallery
                                  </span>
                                  <span className="text-xs text-slate-500">{beach.gallery_count} photos</span>
                                </button>
                              )}

                              {beach.map_url && (
                                <a href={beach.map_url} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 text-sm font-bold text-blue-700 hover:text-blue-900"
                                  data-testid={`beach-map-${beach.slug}`}>
                                  <MapPin className="w-4 h-4" /> Open in Maps
                                </a>
                              )}
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>
                ))
              )}
            </>
          )}
        </div>
      </main>

      {galleryBeach && <GalleryModal beach={galleryBeach} onClose={() => setGalleryBeach(null)} />}

      <Footer />
    </div>
  );
};

export default BeachInfoPage;
